import * as React from "react";
import { Hint, PopupPositionsType } from "react-ui";

import { ControlValidationProps } from "../../Validations/ValidationProps";
import { Tooltip } from "../../Internals/Tooltip/Tooltip";
import { AutoValidationMessage } from "../../../../../Common/AutoValidationUtils";
import { HighlightingOptions } from "../../ExternalHighlighting";

export const lowerCaseFirstLetter = (str: string | undefined): string | undefined => {
    if (str == undefined || str.length === 0) {
        return str;
    }
    return (str[0] ?? "").toLowerCase() + str.slice(1);
};

interface TooltipProps extends ControlValidationProps {
    formula?: React.ReactNode;
    hint?: React.ReactNode;
    autoValue?: string;
    currency?: boolean;
    currencyFractionDigits?: number;
    isTooltipEnabled?: React.ReactNode | string;
    tooltipPosition?: PopupPositionsType;
    tooltipWidth?: string | number;
    children?: React.ReactNode;
    isReadOnly?: boolean;
    externalHighlightingOptions?: HighlightingOptions;
    externalFocused?: boolean;
}

export function WithFxTooltip<T extends TooltipProps>(props: T): React.ReactElement {
    const { error, warning, formula, isTooltipEnabled, autoValue, isReadOnly } = props;

    const highlightingOptions = props.externalHighlightingOptions;

    const tooltipFormula = typeof formula === "string" ? lowerCaseFirstLetter(formula) : formula;
    const [isTooltipOpened, setIsTooltipOpened] = React.useState(false);
    const highlightingText = highlightingOptions?.tooltipText;
    const tooltipMessage = highlightingText ?? (error || warning);
    const tooltipIsNotEmpty = (!isReadOnly && isTooltipEnabled) || highlightingText;

    const tooltipTrigger = tooltipIsNotEmpty ? (isTooltipOpened ? "opened" : "hover&focus") : "closed";

    const handleTooltipClosed = (): void => {
        setIsTooltipOpened(false);
    };

    React.useEffect(() => {
        if (highlightingOptions?.isTooltipFixed !== undefined) {
            setIsTooltipOpened(highlightingOptions?.isTooltipFixed);
        }
    }, [highlightingOptions?.isTooltipFixed]);

    return (
        <Tooltip
            formula={tooltipFormula}
            currencyFractionDigits={props.currencyFractionDigits}
            pos={highlightingText ? "right middle" : (props.tooltipPosition ?? "top left")}
            trigger={tooltipTrigger}
            value={tooltipMessage}
            autoValue={autoValue}
            currency={props.currency}
            combineMessages={tooltipMessage?.includes(AutoValidationMessage)}
            width={props.tooltipWidth}
            closeButton={isTooltipOpened}
            onCloseClick={handleTooltipClosed}
            highlightingTooltipText={highlightingText}>
            {props.hint ? (
                <Hint text={tooltipMessage || tooltipFormula ? undefined : props.hint}>{props.children}</Hint>
            ) : (
                props.children
            )}
        </Tooltip>
    );
}
